/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace variable-name align max-line-length */

import {ProjectElement}           from './project.element';
import {ProjectElementType}       from './project.element.type';
import {Project}                  from '../project';

import {Blob as FileBlob, BlobType} from '../blob';

import {Logger}                   from '@/util/logger';

import {JSONBrowserMetadata}      from '@/screens/ide/json.browser.metadata';

import {first}                    from "rxjs/operators";
import {basename, empty} from "@/util/util";
import {ProjectElementFactoryDecorator} from "@/models/project/project.element.factory.decorator";
import {FileBrowserMetadata} from "@/screens/ide/file.browser.metadata";
import {PropertiesHolder} from "@/models/properties.holder";
import {SimulationJob} from "@/models/sim/simulation.job";

/**
 *
 * @class KPIElement
 *
 * @brief node to maps directly to a KPI summary object for a simulation job
 * (one scenario and one train movement result), the KPI is the set of summary
 * metrics for that scenario/movement pair.  The whole thing together is a
 * triplet.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      June 2020
 *
 */
@ProjectElementFactoryDecorator('KPIElement')
export class KPIElement extends ProjectElement {

  /** the blob key for the scenario */
  public _inputKey: string = '';

  /** the blob key for train movement */
  public _outputKey: string = '';

  /** the blob key for the KPI object */
  public _kpiKey: string = '';

  /** the message to show instead of the usual label */
  public _error: string = '';

  /** pending, complete, failed */
  public _kpiStatus: string = '';

  protected actionKeys: string[] = [
    'jsoneditkpi',
    'downloadkpi',
    'generatekpitable',
    'renamekpi',
    'deletekpi',
    'viewinbrowser'
  ];

  /** standard constructor */
  constructor(
     project: Project,
          id: string,
       input: string,
      output: string,
         kpi: string,
      parent: ProjectElement,
    publish?: boolean,
     peType?: ProjectElementType
  ) {

    super(project, id, peType ? peType : ProjectElementType.KPI);

    this._inputKey    = input;
    this._outputKey   = output;
    this._kpiKey      = kpi;
    this._enabled     = true;
    this._label       = this.generateLabel();
    this._icon        = 'blob-icons/' + FileBlob.iconOfType(BlobType.KPI);
    this._elementKind = 'KPI';

    /* normal child add...*/

    if (parent) {

      this._project.addElement(this, parent, publish);
    }

  }

  private generateLabel(): string {

    /*
     * make a better label; we to include the base name so that its clear what the KPI is for even
     * in the label in the project tree.
     *
     */

    const base: string = basename(this._kpiKey);

    return `${base.substring(0, basename(base).lastIndexOf('.'))}-KPI`;
  }

  /** helper to use to for static global constructors (and factory registration) */
  static requireType() {
    console.log("requiring KPIElement");
  }

  /*
   * when blobs get moved/copied in bulk, project elements have to allow being told where
   * their blobs have been moved to.
   *
   */
  public adjusted(searchKey: string, replaceKey: string) {

    if (this._kpiKey === searchKey) {

      this._kpiKey = replaceKey;
    }

    super.adjusted(searchKey, replaceKey);
  }

  /** fetch any secondary blobs for this element */
  public secondaryBlobs(): string[] {

    if ((this._kpiKey === null) || (this._kpiKey === '')) {

      return [];
    }

    /* KPI elements have an input/output (of the parent result element) but add a kpi blob as well. */

    return [
      this._kpiKey
    ];
  }

  /** set the status and label for the KPI request/element and do any restyling as needed */
  public setStatus(error: string, status: string) {

    this._error     = error;
    this._kpiStatus = status.toLocaleLowerCase().trim();

    if (error == '') {

      this._label = this.generateLabel();

    } else {

      this._label = error;
    }

    this._iconClass = '';
    this._nodeClass = '';

    switch (this._kpiStatus) {

      case 'complete':  this._iconClass = 'fancytree-custom-icon-green';  this._nodeClass = 'element-ok';       break;
      case 'completed': this._iconClass = 'fancytree-custom-icon-green';  this._nodeClass = 'element-ok';       break;
      case 'pending':   this._iconClass = 'fancytree-custom-icon-yellow'; this._nodeClass = 'element-pending';  break;
      case '':          this._iconClass = 'fancytree-custom-icon-yellow'; this._nodeClass = 'element-pending';  break;
      case 'error':     this._iconClass = 'fancytree-custom-icon-red';    this._nodeClass = 'element-failed';   break;
      case 'fail':      this._iconClass = 'fancytree-custom-icon-red';    this._nodeClass = 'element-failed';   break;
      case 'failed':    this._iconClass = 'fancytree-custom-icon-red';    this._nodeClass = 'element-failed';   break;
    }

    /* refresh after label/icon updates */

    this.redraw();
  }

  /** test if the KPI request has been made but isn't complete/fail yet */
  public isPending(): boolean {

    return this._kpiStatus === 'pending';
  }

  public completed(): boolean {

    if (this.isPending()) {

      return false;
    }

    return (this._kpiStatus === 'complete') || (this._kpiStatus === 'completed');
  }

  /** test that we actually have KPI data */
  public hasData(): boolean {

    return !empty(this._kpiKey);
  }

  /** drop data on this element */
  public acceptDrop(data): boolean {

    return false;
  }

  /** is this element deletable? */
  public isDeletable() : boolean {

    return true;
  }

  /** test if any blob of this type can be a child of this element */
  public acceptsBlobType(type: BlobType): boolean {

    return false;
  }

  /** convert this element to a simplified form that can be easily serialized */
  public node(init?: any) {

    return super.node({
      inputkey:  this._inputKey,
      outputkey: this._outputKey,
      kpikey:    this._kpiKey,
      kpistatus: this._kpiStatus,
      kpierror:  this._error
    });
  }

  /** initialize this element given the data in the simplified node object */
  public fromNode(node: any): void {

    /* recover the basics */

    super.fromNode(node);

    /* recover the job details */

    this._inputKey  = node.inputkey;
    this._outputKey = node.outputkey;
    this._kpiKey    = node.kpikey;
    this._error     = node.kpierror;
    this._kpiStatus = node.kpistatus;

    /*
     * if the re-load the page or otherwise abort before the REST request finishes,
     * and we end up reloading, then on re-load, we auto-abort anything still pending,
     * since it now has no way to complete.
     *
     */

    if (this._kpiStatus === 'pending') {

      this._kpiStatus = 'failed';
      this._error     = 'Aborted';
    }

    /* make sure our dynamic label is correct */

    this._label     = this.generateLabel();
  }

  /**
   * sub-classes must add their menu item definition(s) to the given action set,
   * in jquery-contextmenu fashion.
   *
   * @param actionSet any
   *
   */
  public actions(actionSet: any): any {

    actionSet.jsoneditkpi = {
      name: 'View',
      icon: 'fas fa-keyboard',
      disabled: !this.hasData()
    };

    actionSet.downloadkpi = {
      name: 'Download',
      icon: 'fas fa-file-download',
      disabled:  !this.hasData()
    };

    actionSet.viewinbrowser = {
      name: 'Folder Of',
      icon: 'fas fa-list',
      disabled: false
    };

    actionSet.generatekpitable = {
      name: 'Generate KPI Table',
      icon: 'fas fa-file',
      disabled: !this.hasData()
    };

    actionSet.renamekpi = {
      name: 'Rename KPI',
      icon: 'fas fa-tag',
      disabled: !empty(this._error)
    };

    actionSet.deletekpi = {
      name: 'Delete KPI',
      icon: 'fas fa-trash',
      disabled: false
    };

    return actionSet;
  }

  /** test if this element accepts the given action */
  public acceptsAction(key: string): boolean {

    return !!this.actionKeys.find(x => x === key);
  }

  /** append these elements properties to the holder */
  public properties(properties?: PropertiesHolder): PropertiesHolder {

    if (!properties) {

      properties = new PropertiesHolder();

    }

    properties.set('Kind',   this._elementKind);
    properties.set('ID',     this._id);
    properties.set('Name',   this._label);
    properties.set('Key',    this._kpiKey);
    properties.set('Status', this._kpiStatus);
    properties.set('Input',  this._inputKey);
    properties.set('Errors', this._error);

    /* add in the usual properties */

    return super.properties(properties);
  }

  /** subclasses must implement their own actions */
  public action(key: string, selection: ProjectElement[]) {

    if ( !this.actionKeys.find(x => x === key) ) {

      /* let ProjectElement handle it */

      this._parent.action(key, selection);

      return ;
    }

    switch (key) {

      case 'generatekpitable': {

        this._project.generateKPITable(this);
      }
      break;

      case 'jsoneditkpi': {

        Logger.debug('[KPIElement] JSON edit', this._kpiKey, this._blobType);

        this._project.services.ide.launchWindow(new  JSONBrowserMetadata(
          '',
          '',
          this._kpiKey,
          'json',
          basename(this._kpiKey),
          '<i class="fas fa-edit"></i>'
        ));

      }
      break;

      case 'downloadkpi': {

        Logger.debug('[KPIElement] downloading', this._kpiKey, this._blobType);

        this.downloadKPI();
      }
      break;

      case 'viewinbrowser': {

        this._project.services.ide.launchWindow(FileBrowserMetadata.build(this._kpiKey, this._kpiKey));

        break;
      }

      case 'renamekpi': {

        if (!empty(this._error)) {

          break;
        }

        /*
         * KPI  elements don't have children, and their path isn't relative to the direct parent,
         * so all we have to do is allow for label editing...except...when the KPI was generated
         * with an error...because in that case we use the label to show the error.
         *
         */

        const kpi: KPIElement = this;

        kpi._project.services.modals.open(
          'rename-element-modal',
          (name, resolve, reject) => {

            name = name.trim();

            if (empty(name)) {

              reject('KPI name can not be empty.');

              return;
            }

            Logger.debug('[KPIElement] validating: ', name);

            /* make sure we aren't name clashing ...  */

            const peers: ProjectElement[] = kpi._parent._children;

            for (const peer of peers) {

              if (peer._label === name) {

                reject('KPI of that name already exists.');

                return;
              }

            }

            resolve(name);

          },
          (name): void => {

            /* new label was accepted */

            Logger.debug('[KPIElement] renaming: ', name);

            kpi._label = `${name.trim()}`;

            kpi.redraw();

            kpi.save();
          }, {
            title: 'Rename KPI',
            placeholder: this._label
          });

      }
      break;

      case 'deletekpi': {

        Logger.debug('[KPIElement] deleting', this._kpiKey, this._blobType);

        this.deleteElements(selection);
      }
      break;

    }
  }

  /** helper to delete any cached artifacts from the remote simulation */
  public deleteKPI(selection: ProjectElement[]) {

    if (this.pendingKeys.length > 0) {

      /* busy right now */

      return ;
    }

    /*
     * the cached results are internal to a "result", we just need to batch them up,
     * and remove them all with a fork/join.  In our case there are just two artifacts,
     * the log and the output.
     *
     */

    this.pendingKeys = [];

    this.pendingKeys.push([this._kpiKey,    'latest']);

    Logger.debug('[KPIElement] deleting blobs: ', this.pendingKeys);

    this._project.services.blobber.remove(this.pendingKeys[0][0], this.pendingKeys[0][1])
      .pipe(first())
      .subscribe(
        (results: string) => {

          /* everything got deleted ok, add the children in the project and save the latest project */

          Logger.debug('[KPIElement] blobs deleted: ', results);

          /* make sure the project is saved */

          this.pendingKeys = [];

          /* delete self */

          this.deleteElements([this]);

        },
        (error) => {

          Logger.error('[KPIElement] problems deleting blobs: ', error);

        });

  }

  public downloadKPI() {

    Logger.debug('[KPIElement] downloading', this._kpiKey, this._blobType);

    let   mime: string   = 'application/json';

    let keyBase = this._kpiKey.substring(this._kpiKey.lastIndexOf('/') + 1);

    if (!keyBase || keyBase === '' ) {

      keyBase = this._blobKey;
    }

    /* fetch the blob */

    this._project.services.blobber.contents(this._kpiKey)
      .pipe(first())
      .subscribe(
        (content: string) => {

          Logger.debug(`[KPIElement] downloading ${keyBase} => ${mime}`);

          const blob = new Blob([content], { type: mime });
          const url  = window.URL.createObjectURL(blob);

          /* set up a fake one-time link with the right download name and the blob attached as the download */

          const element: HTMLAnchorElement = document.createElement('a');
          element.href          = url;
          element.download      = keyBase || 'download';

          element.style.display = 'none';

          document.body.appendChild(element);

          /* do it */

          element.click();

          /* clean up */

          document.body.removeChild(element);

        },
        (error) => {

          Logger.error(`[KPIElement] can't find content ${this._blobKey}: `, error);
        });

  }
}
